import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["input", "preview", "existingInputs"]

    connect() {
        // entries: { key, kind: "new"|"existing", file?, id?, name, size, url? }
        this.entries = []
        this.setupPickerInput()
        this.fetchDefaultAttachments()
    }

    handleFiles(event) {
        const newFiles = Array.from(event.target.files)

        newFiles.forEach((file) => {
            const key = `new:${crypto.randomUUID()}`
            this.entries.push({
                key,
                kind: "new",
                file,
                name: file.name,
                size: file.size,
            })

            this.previewTarget.appendChild(
                this.createFilePreview({
                    key,
                    name: file.name,
                    size: file.size,
                    url: null,
                })
            )
        })

        this.updateFileList()

        // Allow selecting the same file again by clearing ONLY the picker input.
        // Never clear `inputTarget` because it holds the FileList used for form submission.
        if (this.pickerInput && event.target === this.pickerInput) {
            this.pickerInput.value = ""
        }
    }

    removeByKey(key) {
        // Remove from entries
        const idx = this.entries.findIndex((e) => e.key === key)
        if (idx === -1) return

        const entry = this.entries[idx]
        this.entries.splice(idx, 1)

        // If it was an existing attachment, remove its hidden input
        if (entry.kind === "existing") {
            const hidden = this.existingInputsTarget.querySelector(
                `input[data-attachment-key="${CSS.escape(key)}"]`
            )
            hidden?.remove()
        }

        // Remove preview
        const previewEl = this.previewTarget.querySelector(
            `.single-attachment-preview[data-attachment-key="${CSS.escape(key)}"]`
        )
        previewEl?.remove()

        // Rebuild FileList for NEW files only
        this.updateFileList()
    }

    async fetchDefaultAttachments() {
        const namespace = document.querySelector("body").getAttribute("namespace")
        const sgid = this.element.dataset.sgid

        if (!sgid) {
            console.warn("SGID is missing.")
            return
        }

        try {
            const response = await fetch(`/${namespace}/email_messages/default_attachments?sgid=${encodeURIComponent(sgid)}`)
            const attachments = await response.json()

            // Expect attachments like:
            // [{ signed_id, name, size, url }, ...]
            attachments.forEach((a) => {
                const id = a.signed_id
                if (!id) {
                    console.warn("Attachment missing signed_id:", a)
                    return
                }

                const key = `existing:${id}`

                this.entries.push({
                    key,
                    kind: "existing",
                    id,
                    name: a.name,
                    size: a.size,
                    url: a.url,
                })

                // Hidden input to tell server “keep this attachment”
                const input = document.createElement("input")
                input.type = "hidden"
                input.name = "email_message[existing_signed_ids][]" // adjust to your params
                input.value = id
                input.dataset.attachmentKey = key
                this.existingInputsTarget.appendChild(input)

                // Preview (with link)
                this.previewTarget.appendChild(
                    this.createFilePreview({
                        key,
                        name: a.name,
                        size: a.size,
                        url: a.url,
                    })
                )
            })

            // Only NEW files belong to the file input
            this.updateFileList()
        } catch (error) {
            console.error("Error fetching default attachments:", error)
        }
    }

    updateFileList() {
        const dt = new DataTransfer()
        this.entries
            .filter((e) => e.kind === "new")
            .forEach((e) => dt.items.add(e.file))

        this.inputTarget.files = dt.files
    }

    setupPickerInput() {
        // We keep `inputTarget` as the submission input (it holds the DataTransfer FileList).
        // We create a separate hidden picker input purely to open the file dialog.
        // This lets us reset picker.value = "" to allow selecting the same file again.
        if (this.pickerInput) return

        const picker = this.inputTarget.cloneNode(true)

        // Prevent the picker from being submitted.
        picker.removeAttribute("name")

        // Avoid duplicate IDs if present.
        picker.removeAttribute("id")

        // Keep it hidden like the original.
        picker.classList.add("hidden")

        // Ensure change events go to this controller method.
        picker.addEventListener("change", (e) => this.handleFiles(e))

        // Insert right after the real input.
        this.inputTarget.insertAdjacentElement("afterend", picker)

        this.pickerInput = picker
    }

    openFileDialog() {
        (this.pickerInput || this.inputTarget).click()
    }

    createFilePreview({key, name, size, url}) {
        const fileDiv = document.createElement("div")
        fileDiv.className = "single-attachment-preview"
        fileDiv.dataset.attachmentKey = key

        const nameSpan = document.createElement("span")
        nameSpan.className = "flex-grow text-gray-800 dark:text-gray-100"

        if (url) {
            const link = document.createElement("a")
            link.href = url
            link.textContent = name
            link.className = "hover:text-blue-400"
            link.target = "_blank"
            nameSpan.appendChild(link)
        } else {
            nameSpan.textContent = name
        }

        const sizeSpan = document.createElement("span")
        sizeSpan.className = "text-sm text-gray-400"
        sizeSpan.textContent = typeof size === "number" ? this.formatFileSize(size) : size

        const removeSpan = document.createElement("span")
        removeSpan.className = "text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 cursor-pointer transition-colors duration-200 text-white"
        removeSpan.textContent = "Remove"
        removeSpan.addEventListener("click", () => this.removeByKey(key))

        fileDiv.appendChild(nameSpan)
        fileDiv.appendChild(sizeSpan)
        fileDiv.appendChild(removeSpan)

        return fileDiv
    }

    formatFileSize(bytes) {
        if (bytes === 0) return "0 Bytes"
        const k = 1024
        const sizes = ["Bytes", "KB", "MB", "GB"]
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`
    }
}
